"use client";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Card, CircularProgress, MenuItem, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import ContactsModal from "@/components/screens/Chat/components/ContactsModal";
import { getConversationReport } from "@/api/conversation";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import { useNotification } from "@/contexts/notification-context";
import useToast from "@/contexts/toast-context";
import useGetConversationReport from "@/hooks/requests/conversations/use-get-conversation-report";
import useStartConversation from "@/hooks/requests/conversations/use-start-conversation";
import useGetAllPlatforms from "@/hooks/requests/platform/use-get-all-platform";
import usePagination from "@/hooks/use-pagination";
import IContact from "@/interfaces/contact.interface";
import { CHAT_CONTACT_LIST_MESSAGES } from "@/messages/chat/contact-list.messages";
import { ConversationReportMessages } from "@/messages/conversation-report/conversation-report.messages";
import {
  CONVERSATION_REPORT_EXPORT_LIMIT,
  ConversationReportForm,
  EMPTY_CONVERSATION_REPORT_FORM,
  IConversationReportRow,
  toConversationReportQuery,
} from "@/types/conversation-report.types";

import ConversationReportFilters, { fieldSx } from "./components/Filters";
import ConversationReportTable from "./components/Table";
import { buildConversationReportCsv, downloadConversationReportCsv } from "./csv";

type Props = {
  messages: ConversationReportMessages;
};

function ConversationReport({ messages }: Props) {
  const pagination = usePagination({ initialPageSize: 10 });
  const { pageNumber, pageSize, changePage } = pagination;
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { activeChatPlatformId } = useNotification();
  const platforms = useGetAllPlatforms({ pageNumber: 1, pageSize: 20 });
  const { startConversation, isLoading: isStarting } = useStartConversation();
  const [draft, setDraft] = useState<ConversationReportForm>(EMPTY_CONVERSATION_REPORT_FORM);
  const [applied, setApplied] = useState<ConversationReportForm>(EMPTY_CONVERSATION_REPORT_FORM);
  const [showMore, setShowMore] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const query = toConversationReportQuery(applied);
  const { data, isLoading, error } = useGetConversationReport({ pageNumber, pageSize, query });

  const shouldLoadTable = !!data && !error && data.items.length > 0;
  const shouldLoadEmpty = !!data && !isLoading && !error && data.items.length === 0;

  const applyFilters = () => {
    setApplied(draft);
    changePage(0);
  };

  const clearFilters = () => {
    setDraft(EMPTY_CONVERSATION_REPORT_FORM);
    setApplied(EMPTY_CONVERSATION_REPORT_FORM);
    changePage(0);
  };

  const exportCsv = async () => {
    setIsExporting(true);
    try {
      const exportPageSize = CONVERSATION_REPORT_EXPORT_LIMIT;
      const rows: IConversationReportRow[] = [];
      let pageNumberToFetch = 1;
      // Busca todas as páginas do filtro atual, sem limite de quantidade.
      for (;;) {
        const page = await getConversationReport(query, { pageNumber: pageNumberToFetch, pageSize: exportPageSize });
        const items = page.items ?? [];
        rows.push(...items);
        const done = !page.hasNextPage || items.length === 0 || rows.length >= page.totalCount;
        if (done) break;
        pageNumberToFetch += 1;
      }
      downloadConversationReportCsv(buildConversationReportCsv(rows));
    } catch {
      showToast({ text: messages.loadError, type: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  const platformId = activeChatPlatformId ?? platforms.data?.items[0]?.id;

  const startNewConversation = async (contact: IContact) => {
    if (contact.active === false) {
      showToast({ text: CHAT_CONTACT_LIST_MESSAGES.startConversationInactive, type: "error" });
      return;
    }
    if (typeof platformId !== "number") {
      showToast({ text: CHAT_CONTACT_LIST_MESSAGES.startConversationError, type: "error" });
      return;
    }

    try {
      await startConversation({
        formData: { contactId: contact.id, platformId },
        successMessage: CHAT_CONTACT_LIST_MESSAGES.startConversationSuccess,
        errorMessage: CHAT_CONTACT_LIST_MESSAGES.startConversationError,
        onSuccess: () => {
          setShowContacts(false);
          navigate(ROUTE_URLS.chat(lang));
        },
        onError: () => setShowContacts(false),
      });
    } catch {
      setShowContacts(false);
    }
  };

  return (
    <PageLayout
      messages={messages}
      title={messages.pageTitle}
      breadCrumbs={[
        { title: messages.customer_service, href: ROUTE_URLS.conversationReport(lang) },
        { title: messages.breadCrumbTitle, href: ROUTE_URLS.conversationReport(lang) },
      ]}
    >
      <PageContainer>
        <Card sx={{ mt: 2 }}>
          <MDBox p={2}>
              <ConversationReportFilters
                form={draft}
                messages={messages}
                showMore={showMore}
                onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
              />
              <Grid container spacing={3} justifyContent="flex-end" mt={3}>
                <Grid size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <MDButton variant="outlined" color="dark" fullWidth onClick={() => setShowMore((value) => !value)}>
                    {showMore ? messages.lessFilters : messages.moreFilters}
                  </MDButton>
                </Grid>
                <Grid size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <MDButton variant="gradient" color="light" fullWidth onClick={clearFilters}>
                    {messages.clearFilters}
                  </MDButton>
                </Grid>
                <Grid size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <MDButton variant="gradient" color="dark" fullWidth onClick={applyFilters}>
                    {messages.applyFilters}
                  </MDButton>
                </Grid>
              </Grid>
          </MDBox>
        </Card>

        <Card sx={{ mt: 2 }}>
          <MDBox p={2}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              mb={2}
            >
              <MDTypography variant="h6">{messages.resultsTitle}</MDTypography>
              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <MDTypography variant="caption" fontWeight="medium" color="dark">
                  {messages.sortBy}
                </MDTypography>
                <TextField
                  select
                  size="small"
                  slotProps={{ select: { IconComponent: KeyboardArrowDownIcon } }}
                  value={draft.sortBy}
                  onChange={(event) => {
                    const sortBy = event.target.value;
                    setDraft((current) => ({ ...current, sortBy }));
                    setApplied((current) => ({ ...current, sortBy }));
                  }}
                  sx={{ ...fieldSx, minWidth: 250, "& .MuiSelect-select": { pr: "36px !important" } }}
                >
                  <MenuItem value="lastReceivedDate">{messages.lastReceivedDate}</MenuItem>
                  <MenuItem value="startDate">{messages.startDate}</MenuItem>
                  <MenuItem value="lastInterationDate">{messages.lastInterationDate}</MenuItem>
                  <MenuItem value="firstAnswerDate">{messages.firstAnswerDate}</MenuItem>
                  <MenuItem value="endDate">{messages.endDate}</MenuItem>
                  <MenuItem value="csat">{messages.csat}</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  slotProps={{ select: { IconComponent: KeyboardArrowDownIcon } }}
                  value={draft.sortDirection}
                  onChange={(event) => {
                    const sortDirection = event.target.value as ConversationReportForm["sortDirection"];
                    setDraft((current) => ({ ...current, sortDirection }));
                    setApplied((current) => ({ ...current, sortDirection }));
                  }}
                  sx={{ ...fieldSx, minWidth: 150 }}
                >
                  <MenuItem value="desc">{messages.sortDesc}</MenuItem>
                  <MenuItem value="asc">{messages.sortAsc}</MenuItem>
                </TextField>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={exportCsv}
                  disabled={isExporting}
                  isLoading={isExporting}
                  startIcon={<FileDownloadOutlinedIcon />}
                  sx={{ minWidth: 130 }}
                >
                  {messages.export}
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={() => setShowContacts(true)}
                  disabled={isStarting}
                  startIcon={<AddCircleOutlineIcon />}
                >
                  {messages.newConversation}
                </MDButton>
              </MDBox>
            </MDBox>

            <Suspense isSuspended={isLoading && !data} fallback={<CircularProgress size={40} />}>
              {shouldLoadTable && data && (
                <ConversationReportTable data={data} pagination={pagination} messages={messages} />
              )}
              {!!error && (
                <AlertBlock title={messages.loadError} description={error.message} alt={messages.loadError} />
              )}
              {shouldLoadEmpty && (
                <AlertBlock
                  title={messages.emptyTitle}
                  description={messages.emptyDescription}
                  alt={messages.emptyTitle}
                />
              )}
            </Suspense>
          </MDBox>
        </Card>
        {showContacts && (
          <ContactsModal
            messages={CHAT_CONTACT_LIST_MESSAGES}
            onClose={() => setShowContacts(false)}
            onSelect={startNewConversation}
          />
        )}
      </PageContainer>
    </PageLayout>
  );
}

export default ConversationReport;
